<template>
  <div class="pt-4 px-4 pb-20">
    <div class="mb-5">
      <h3 class="text-3xl font-bold mb-3">{{ $t("referral_program") }}</h3>
      <router-link to="/ref-stats" class="flex items-center text-sm font-medium">
        {{ $t("read_more") }}
        <ArrowUpIcon class="w-4 ml-1" :class="[$theme.IconSvgColor]" />
      </router-link>
    </div>

    <a
      :href="shareLink"
      class="ref-up mb-8 mt-5 flex w-full rounded-xl relative"
    >
      <div class="w-2/3 text-sm text-black p-5">
        <div class="mb-4 text-xl font-semibold">
          {{ $t("earn_up_to_25") }}
        </div>
        <div
          class="border border-black inline-flex items-center rounded-full px-4 py-2 text-lg font-medium"
        >
          {{ $t("invite_friend") }}
          <ArrowUpIcon class="w-6 stroke-blue-700" />
        </div>
      </div>
      <div class="absolute bottom-4 right-0">
        <img src="/img/gift.svg" />
      </div>
    </a>

    <div
      class="rounded-xl flex flex-col w-full items-center py-2 px-4 border"
      :class="[$theme.cardBg, $theme.borderBlockColor]"
    >
      <div class="text-center py-4">
        <div class="text-4xl font-medium">$ {{ availableForWithdrawal }}</div>
        <div class="mt-1 opacity-60 mb-3">{{ $t("available") }}</div>
        <button
          @click="onOpenCreateWithdraw"
          class="w-full py-1.5 rounded-lg flex items-center justify-center"
        >
          <div class="font-semibold">
            {{ $t("withdraw") }}
          </div>
          <ArrowUpIcon class="w-5 h-5 ml-2" :class="[$theme.IconSvgColor]" />
        </button>
      </div>
      <div
        class="text-center border-t flex items-center justify-between m-light-card-border-color w-full py-2"
      >
        <div class="">{{ $t("total_earned") }}</div>
        <div class="font-bold">$ {{ totalReferralPayout }}</div>
      </div>
    </div>

    <!-- добавить превод -->
    <div class="text-xl font-semibold mt-6">Your referral code:</div>
    <div :class="[$theme.cardBg, $theme.borderBlockColor, 'rounded-xl mt-1 px-3 py-2 border']">
      <div class="flex items-center justify-between">
        <input
          ref="ref_code"
          class="bottom-0 p-0 border-transparent focus:outline-none rounded-lg bg-transparent"
          :value="$user.referral_code"
          readonly
        />
        <button
          @click="copyToClipboard"
          class="flex items-center font-medium"
          :class="[$theme.navActiveTextColor2]"
        >
          <CopyIcon class="w-5 mr-2" />
          {{ $t("copy") }}
        </button>
      </div>
    </div>

    <div class="mb-3 mt-5 block">
      <nav
        class="grid grid-cols-2 space-x-4 mb-5 bg-white rounded-full p-1"
        :class="[$theme.tabsContainerBg]"
        aria-label="Tabs"
      >
        <a
          v-for="tab in tabs"
          :key="tab.key"
          @click="onChangeTab(tab.key)"
          href="javascript:void(0)"
          :class="[
            activeTab === tab.key ? `m-yellow-bg text-black` : `${$theme.tabTextColor}`,
            'rounded-full px-3 py-2 text-sm font-medium text-center',
          ]"
        >
          {{ tab.name }}
        </a>
      </nav>
    </div>

    <template v-if="loading">
      <div class="mt-5 rounded-xl" :class="[$theme.cardBg]">
        <LoadingBlock />
      </div>
    </template>
    <template v-else>
      <template v-if="activeTab === 'refs'">
        <template v-if="referrers.data.length > 0">
          <div
            v-for="(referrer, rIdx) in referrers.data"
            :key="referrer.id"
            class="py-2 m-light-card-border-color"
            :class="[rIdx !== 0 ? 'border-t' : '']"
          >
            <ReferralItem :item="referrer" :level="1" />
          </div>
        </template>
        <template v-else>
          <div
            class="mt-5 rounded-xl overflow-hidden px-4 py-4 border"
            :class="[$theme.cardBg, $theme.borderBlockColor]"
          >
            <p class="text-center">{{ $t("you_have_no_referrals") }}</p>
          </div>
        </template>
      </template>

      <template v-if="activeTab === 'history'">
        <div class="rounded-xl bg-yellow-100 p-4 mb-4 hidden">
          <div class="flex">
            <div class="flex-shrink-0">
              <InformationCircleIcon
                class="h-5 w-5 text-yellow-600"
                aria-hidden="true"
              />
            </div>
            <div class="ml-3 flex-1 md:flex md:justify-between">
              <p class="text-sm text-yellow-700">
                {{ $t("processing_request_withdraw") }}
              </p>
            </div>
          </div>
        </div>

        <template
          v-if="withdrawalRequests && withdrawalRequests.data.length > 0"
        >
          <div
            v-for="(
              withdrawalRequest, withdrawalRequestIdx
            ) in withdrawalRequests.data"
            :key="withdrawalRequest.id"
            class="py-3 flex items-center justify-between"
            :class="[
              withdrawalRequestIdx !== 0 ? 'border-t' : '',
              $theme.cardBorderColor,
            ]"
          >
            <div class="flex items-center">
              <div
                class="w-10 h-10 rounded-full border flex items-center justify-center"
                :class="[$theme.cardBorderColor]"
              >
                <DownloadIcon class="w-6 h-6 rounded-full" />
              </div>
              <div class="pl-2">
                <div class="font-medium">{{ withdrawalRequest.amount }}</div>
                <div class="text-sm opacity-50">
                  {{ withdrawalRequest.created_at_format }}
                </div>
              </div>
            </div>
            <div class="flex flex-col items-end">
              <div class="text-sm">
                <template v-if="withdrawalRequest.status === 'pending'">
                  <div
                    class="flex items-center gap-2 rounded-full px-2 py-0.5"
                    :class="[$theme.tabsContainerBg]"
                  >
                    <div class="w-2 h-2 rounded-full bg-yellow-500"></div>
                    <span class="text-sm">
                      {{ $t("pending") }}
                    </span>
                  </div>
                </template>
                <template v-if="withdrawalRequest.status === 'approved'">
                  <div
                    class="flex items-center gap-2 rounded-full px-2 py-0.5"
                    :class="[$theme.tabsContainerBg]"
                  >
                    <div
                      class="w-2 h-2 rounded-full"
                      :class="[$theme.bgSuccessPnlColor]"
                    ></div>
                    <span class="text-sm">
                      {{ $t("approved") }}
                    </span>
                  </div>
                </template>
                <template v-if="withdrawalRequest.status === 'rejected'">
                  <div
                    class="flex items-center gap-2 rounded-full px-2 py-0.5"
                    :class="[$theme.tabsContainerBg]"
                  >
                    <div
                      class="w-2 h-2 rounded-full"
                      :class="[$theme.bgDangerColor]"
                    ></div>
                    <span class="text-sm">
                      {{ $t("rejected") }}
                    </span>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="mt-5 overflow-hidden px-4 py-4">
            <p class="text-center">{{ $t("no_withdraw") }}</p>
          </div>
        </template>
      </template>
    </template>
  </div>

  <TransitionRoot as="template" :show="isOpenCreateWithdraw">
    <Dialog class="relative z-50" @close="isOpenCreateWithdraw = false">
      <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      <div class="fixed inset-0 z-50 flex items-end justify-center">
        <TransitionChild
          as="template"
          enter="transform transition ease-in-out duration-300"
          enter-from="translate-y-full opacity-0"
          enter-to="translate-y-0 opacity-100"
          leave="transform transition ease-in-out duration-300"
          leave-from="translate-y-0 opacity-100"
          leave-to="translate-y-full opacity-0"
        >
          <DialogPanel
            class="w-full rounded-t-2xl px-4 pt-5 pb-4 text-left shadow-xl sm:max-w-lg sm:w-full sm:p-6"
            :class="[$theme.secondBaseBg]"
          >
            <template v-if="wallets.length">
              <div>
                <div class="mb-3">
                  <DialogTitle as="h3" class="text-sm leading-6" :class="[$theme.baseTextColor]">
                    {{ $t("available_you") }}
                  </DialogTitle>
                  <div
                    class="text-3xl font-semibold"
                    :class="[
                      availableForWithdrawal > 0
                        ? ''
                        : 'opacity-50 pointer-events-none',
                    ]"
                  >
                    {{ availableForWithdrawal }} $
                  </div>
                </div>

                <fieldset aria-label="Address">
                  <div class="text-sm mb-2">
                    {{ $t("select_wallet_address") }}
                  </div>
                  <RadioGroup
                    v-model="createWithdrawalRequestForm.wallet_id"
                    class="space-y-2"
                  >
                    <RadioGroupOption
                      as="template"
                      v-for="wallet in wallets"
                      :key="wallet.id"
                      :value="wallet.id"
                      :aria-label="wallet.label"
                      :aria-description="`${wallet.id}, ${wallet.label}`"
                      v-slot="{ active, checked }"
                    >
                      <div
                        :class="[
                          active
                            ? 'border-blue-600 ring-2 ring-blue-600'
                            : 'border-gray-300',
                          'relative block cursor-pointer rounded-xl border bg-white px-3 py-2 shadow-sm focus:outline-none sm:flex sm:justify-between',
                        ]"
                      >
                        <span class="flex items-center">
                          <span class="flex flex-col text-sm">
                            <span
                              class="font-medium"
                              :class="[$theme.baseTextColor]"
                            >{{
                              wallet.label
                            }}</span>
                            <span class="text-gray-600">
                              <span class="block">{{ wallet.address }}</span>
                            </span>
                          </span>
                        </span>
                        <CheckCircleIcon
                          :class="[
                            wallet.id !== createWithdrawalRequestForm.wallet_id
                              ? 'invisible'
                              : '',
                            'h-5 w-5 text-blue-600 absolute top-2 right-2',
                          ]"
                          aria-hidden="true"
                        />
                        <span
                          :class="[
                            active ? 'border' : 'border-2',
                            checked ? 'border-blue-600' : 'border-transparent',
                            'pointer-events-none absolute -inset-px rounded-xl',
                          ]"
                          aria-hidden="true"
                        />
                      </div>
                    </RadioGroupOption>
                  </RadioGroup>
                </fieldset>
              </div>
              <div class="mt-5 sm:mt-6">
                <button
                  @click="onCreateWithdrawalRequest"
                  type="button"
                  class="flex w-full items-center justify-center px-3 py-3 font-semibold m-yellow-bg rounded-full text-black"
                  :class="[
                    availableForWithdrawal > 0
                      ? ''
                      : 'opacity-50 pointer-events-none',
                    isFormProcessing ? 'pointer-events-none opacity-60' : '',
                  ]"
                >
                  {{ $t("withdraw") }}
                </button>
              </div>
            </template>
            <template v-else>
              <div>
                <div>
                  <DialogTitle
                    as="h3"
                    class="text-base font-semibold leading-6"
                    :class="[$theme.baseTextColor]"
                  >
                    {{ $t("add_wallet") }}
                  </DialogTitle>

                  <div class="mt-3">
                    <label
                      for="wallet_label"
                      class="block text-sm font-medium leading-6"
                      :class="[$theme.baseTextColor]"
                      >{{ $t("name") }}</label
                    >
                    <div class="mt-2">
                      <input
                        v-model="createWalletForm.label"
                        type="text"
                        id="wallet_label"
                        class="block w-full rounded-full border-0 py-2 px-3 ring-0 ring-inset focus:ring-1 focus:ring-inset"
                      :class="[
                        $theme.baseTextColor,
                        $theme.inputPlaceTextColor,
                        $theme.inputRingFocusColor,
                        $theme.inputBorderColor,
                        $theme.inputRingColor,
                        $theme.inputBg,
                      ]"
                      />
                    </div>
                  </div>

                  <div class="mt-3">
                    <label
                      for="wallet_address"
                      class="block text-sm font-medium leading-6"
                      :class="[$theme.baseTextColor]"
                      >{{ $t("usdt_address") }}</label
                    >
                    <div class="mt-2">
                      <input
                        v-model="createWalletForm.address"
                        type="text"
                        id="wallet_address"
                        class="block w-full rounded-full border-0 py-2 px-3 ring-0 ring-inset focus:ring-1 focus:ring-inset"
                      :class="[
                        $theme.baseTextColor,
                        $theme.inputPlaceTextColor,
                        $theme.inputRingFocusColor,
                        $theme.inputBorderColor,
                        $theme.inputRingColor,
                        $theme.inputBg,
                      ]"
                      />
                    </div>
                  </div>

                  <template v-if="formValidateErrors.length">
                    <div class="mt-3 rounded-xl bg-red-50 p-2">
                      <div class="flex">
                        <div class="flex-shrink-0">
                          <XCircleIcon
                            class="h-5 w-5 text-red-400"
                            aria-hidden="true"
                          />
                        </div>
                        <div class="ml-3">
                          <h3 class="text-sm font-medium text-red-800">
                            {{ $t("errors") }}
                          </h3>
                          <div class="mt-2 text-sm text-red-700">
                            <ul role="list" class="list-none space-y-1">
                              <li
                                v-for="(
                                  formValidateError, formValidateErrorIndex
                                ) in formValidateErrors"
                                :key="formValidateErrorIndex"
                              >
                                {{ formValidateError }}
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
              <div class="mt-5 sm:mt-6">
                <button
                  @click="onCreateUserWallet"
                  type="button"
                  class="flex w-full items-center justify-center px-3 py-3 font-semibold m-yellow-bg rounded-full text-black"
                  :class="[
                    isFormProcessing ? 'pointer-events-none opacity-60' : '',
                  ]"
                >
                  {{ $t("add") }}
                </button>
              </div>
            </template>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import ArrowUpIcon from "@/assets/icons/ArrowUpIcon.vue";
import DownloadIcon from "@/assets/icons/DownloadIcon.vue";
import LoadingBlock from "@/components/LoadingBlock.vue";
import ReferralItem from "@/components/ref/ReferralItem.vue";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
  RadioGroup,
  RadioGroupOption,
} from "@headlessui/vue";
import {
  CheckCircleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from "@heroicons/vue/20/solid";
import { formattedAmount } from "@/helpers/numberHelper";
import CopyIcon from "@/assets/icons/CopyIcon.vue";

export default {
  components: {
    CopyIcon,
    InformationCircleIcon,
    CheckCircleIcon,
    LoadingBlock,
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
    XCircleIcon,
    RadioGroup,
    RadioGroupOption,
    ReferralItem,
    ArrowUpIcon,
    DownloadIcon,
  },

  computed: {
    botUrl() {
      return this.$settings.bot_url;
    },

    totalReferralPayout() {
      return this.$user.total_referral_payout;
    },

    availableForWithdrawal() {
      return this.$user.available_for_withdrawal;
    },

    shareLink() {
      if (!this.$user) {
        return "";
      }

      return `https://t.me/share/url?url=${encodeURIComponent(
        `${this.botUrl}?start=${this.$user.referral_code}`
      )}&text=${encodeURIComponent(this.$t("invite_message"))}`;
    },

    copyLinkNoEncode() {
      if (!this.$user) {
        return "";
      }

      if (this.$settings.miniapp_url) {
        return `${`${this.$settings.miniapp_url}?startapp=${this.$user.referral_code}`}&text=${this.$t('invite_message')}`;
      }

      return `${`${this.botUrl}?start=${this.$user.referral_code}`}&text=${this.$t("invite_message")}`;
    },
  },

  methods: {
    copyToClipboard() {
      const copyText = this.copyLinkNoEncode;
      navigator.clipboard
        .writeText(copyText)
        .then(() => {
          alert(copyText);
        })
        .catch((error) => {
          console.error("Ошибка копирования ссылки:", error);
          alert("Error copy link");
        });
    },

    loadUser() {
      this.$api.get("/user").then((result) => {
        this.$user = result.data.data;
      });
    },

    onLoadReferrers() {
      this.loading = true;

      this.$api
        .get("/referrers", {
          params: this.queryParams,
        })
        .then((result) => {
          console.log(result.data);
          this.referrers = result.data;
          this.loading = false;
        });
    },

    onLoadUserWallets() {
      this.$api.get("/user/wallets").then((result) => {
        this.wallets = result.data.data;
      });
    },

    onLoadWithdrawalRequest() {
      this.loading = true;

      this.$api.get("/withdrawals").then((result) => {
        console.log(result.data);
        this.withdrawalRequests = result.data;
        this.loading = false;
      });
    },

    onCreateWithdrawalRequest() {
      this.isFormProcessing = true;

      this.$api
        .post("/withdrawals", {
          wallet_id: this.createWithdrawalRequestForm.wallet_id,
          amount: this.availableForWithdrawal,
        })
        .then((result) => {
          console.log(result);

          this.loadUser();

          this.isFormProcessing = true;
          this.isOpenCreateWithdraw = false;
          this.activeTab = "history";

          this.onLoadWithdrawalRequest();
        });
    },

    onChangeTab(key) {
      console.log("key", key);

      if (key === "refs") {
        console.log("load refs", key);
        this.onLoadReferrers();
      }

      if (key === "history") {
        console.log("load history", key);
        this.onLoadWithdrawalRequest();
      }

      this.activeTab = key;
    },

    onOpenCreateWithdraw() {
      this.isOpenCreateWithdraw = true;

      if (this.wallets.length > 0) {
        this.createWithdrawalRequestForm.wallet_id = this.wallets[0].id;
      }
    },

    onCreateUserWallet() {
      this.validateWalletForm();

      if (this.formValidateErrors.length > 0) {
        return;
      }

      this.isFormProcessing = true;

      this.$api
        .post("/user/wallets", this.createWalletForm)
        .then((response) => {
          console.log(response);

          this.onLoadUserWallets();
          // this.isOpenCreateWithdraw = false;
        })
        .catch((error) => {
          console.log("Ошибка при добавлении кошелька", error);
        });
    },

    validateWalletForm() {
      this.formValidateErrors = [];

      if (!this.createWalletForm.label) {
        this.formValidateErrors.push(this.$t("empty_name_wallet"));
      }

      if (!this.createWalletForm.address) {
        this.formValidateErrors.push(this.$t("empty_address_wallet"));
      }

      const addressRegex = /^[a-zA-Z0-9]{34}$/;
      if (!addressRegex.test(this.createWalletForm.address)) {
        this.formValidateErrors.push(this.$t("invalid_address_wallet"));
      }
    },
  },

  data() {
    return {
      loading: true,
      referrers: null,
      withdrawalRequests: null,
      wallets: [],

      activeTab: "refs",
      tabs: [
        { name: this.$t("referrals"), key: "refs" },
        { name: this.$t("history_withdraw"), key: "history" },
      ],

      isOpenCreateWithdraw: false,

      isFormProcessing: false,
      formValidateErrors: [],
      createWalletForm: {
        label: "",
        address: "",
        currency: "USDT",
        network: "TRC20",
      },

      createWithdrawalRequestForm: {
        wallet_id: null,
        amount: 0,
      },

      queryParams: {
        level: 1,
      },

      formattedAmount,
    };
  },

  mounted() {
    this.onLoadReferrers();
    this.onLoadUserWallets();
  },
};
</script>
